import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import NewScenarioModal from "./NewScenarioModal";
import { FiTrash2, FiCopy, FiRotateCcw } from "react-icons/fi";
import { BsPencil } from "react-icons/bs";
import { IoIosAdd } from "react-icons/io";
import { FaFileImport, FaFileExport } from "react-icons/fa";
import ScenarioJsonImporter from "./ScenarioJsonImporter";
import { useCurrentTenant } from "../../hooks/useCurrentTenant";
import "./Scenarios.css";

const Scenarios = () => {
  const currentTenantId = useCurrentTenant();
  const [scenarios, setScenarios] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [showNewScenarioModal, setShowNewScenarioModal] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState(null); // { type: "ok" | "info" | "error", text }
  const navigate = useNavigate();

  const fetchScenarios = async () => {
    try {
      const { data } = await api.get("/scenario");
      const list = Array.isArray(data) ? data : [];
      setScenarios(list.filter((p) => p?.isTemplate !== true));
    } catch (err) {
      console.error("Failed to fetch scenarios:", err);
    }
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  // Hide the restore message after a few seconds
  useEffect(() => {
    if (!restoreMessage) return;
    const t = setTimeout(() => setRestoreMessage(null), 6000);
    return () => clearTimeout(t);
  }, [restoreMessage]);

  const tenantScenarios = useMemo(
    () => scenarios.filter((s) => s.tenantId === currentTenantId),
    [scenarios, currentTenantId]
  );

  /* ---------- Selection ---------- */
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = (rows) => setSelectedIds(new Set(rows.map((p) => p.id)));
  const clearSelection = () => setSelectedIds(new Set());

  /* ---------- Restore default scenarios ---------- */
  // Re-creates only the default scenarios that are missing (deleted).
  // Existing ones - including edited defaults - are never touched.
  const handleRestoreDefaults = async () => {
    setRestoring(true);
    setRestoreMessage(null);
    try {
      const { data } = await api.post("/scenario/defaults/restore");
      const restored = data?.restored ?? [];
      await fetchScenarios();
      setRestoreMessage(
        restored.length
          ? { type: "ok", text: `Restored: ${restored.join(", ")}` }
          : { type: "info", text: "All default scenarios already exist." }
      );
    } catch (err) {
      console.error("Restore default scenarios failed:", err);
      setRestoreMessage({ type: "error", text: "Default scenarios could not be restored." });
    } finally {
      setRestoring(false);
    }
  };

  /* ---------- Bulk Delete ---------- */
  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} scenario(s)?`)) return;

    try {
      for (const id of selectedIds) {
        await api.delete(`/scenario/${id}`);
      }
      await fetchScenarios();
      clearSelection();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      alert("Some scenarios could not be deleted.");
    }
  };

  /* ---------- Filtering ---------- */
  const filteredScenarios = useMemo(() => {
    const q = search.toLowerCase();
    return tenantScenarios.filter((p) => p?.name?.toLowerCase().includes(q));
  }, [tenantScenarios, search]);

  const handleCreateScenario = async ({ name, description, templateId }) => {
    try {
      // Copy of one of the standard scenarios
      if (templateId) {
        const { data: copy } = await api.post(`/scenario/${templateId}/copy`, {
          name,
          description,
        });
        await fetchScenarios();
        navigate(`/scenarios/${copy.id}`);
        return;
      }

      // Empty scenario
      const { data: scenario } = await api.post("/scenario", {
        name,
        tenantId: "",
        description,
        settings: {
          tolerance: 0,
          fillMountingSpace: 200,
          minDistanceWireDuct: 10,
          orientation: 0,
          connection: "",
          placementOptions: {
            enableSubCabinetFill: true,
            bandSeparatorCableDuctWidthMm: 40,
            requireCurrentRowGEQNextFirstRow: false,
            emitBandSeparatorCableDuct: true,
          },
        },
        sections: [],
        sequences: [],
        subGroupSequenceGroups: [],
      });

      navigate(`/scenarios/${scenario.id}`);
    } catch (error) {
      console.error("Failed to create scenario:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this scenario?")) return;

    try {
      await api.delete(`/scenario/${id}`);
      await fetchScenarios();
      clearSelection();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("❌ Scenario could not be deleted.");
    }
  };

  const handleCopy = async (id) => {
    try {
      const { data: scenario } = await api.post(`/scenario/${id}/copy`);
      await fetchScenarios();
      alert("✅ Scenario copied successfully.");
      navigate(`/scenarios/${scenario.id}`);
    } catch (err) {
      console.error("Copy failed:", err);
      alert("❌ Could not copy scenario.");
    }
  };

  const handleExportScenario = async (id) => {
    try {
      const res = await api.get(`/scenario/${id}/export`);

      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: "application/json",
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Scenario_${id}.json`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed");
    }
  };

  return (
    <div className="project-table-container">
      <h2>Scenarios</h2>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "1rem",
        }}
      >
        <button className="btn-newproject" onClick={() => setShowNewScenarioModal(true)}>
          <span><IoIosAdd /></span> New Scenario
        </button>

        <div className="scenario-actions">
          {/* RESTORE DEFAULTS */}
          <button
            className="btn-importscenario btn-restore-defaults"
            title="Re-create default scenarios that were deleted"
            onClick={handleRestoreDefaults}
            disabled={restoring}
          >
            <FiRotateCcw className={restoring ? "spin" : undefined} />
            {restoring ? " Restoring…" : " Restore defaults"}
          </button>

          {/* IMPORT */}
          <button
            className="btn-importscenario"
            title="Import Scenario from JSON"
            onClick={() => document.getElementById("scenario-json-importer").click()}
          >
            <FaFileImport /> Import
          </button>

          <button
            className="btn-delete-all"
            disabled={!selectedIds.size}
            onClick={handleBulkDelete}
          >
            <span><FiTrash2 /></span> Delete Selected ({selectedIds.size})
          </button>
        </div>
      </div>

      {restoreMessage && (
        <div className={`restore-defaults-msg restore-defaults-msg--${restoreMessage.type}`} role="status">
          {restoreMessage.text}
        </div>
      )}

      {/* Table */}
      <table className="section-table">
        <thead>
          <tr>
            <th className="checkbox-column">
              <input
                type="checkbox"
                checked={
                  filteredScenarios.length > 0 &&
                  selectedIds.size === filteredScenarios.length
                }
                onChange={(e) =>
                  e.target.checked ? selectAll(filteredScenarios) : clearSelection()
                }
              />
            </th>
            <th>Name</th>
            <th>Created</th>
            <th>Modified</th>
            <th className="actions-column">Actions</th>
          </tr>
        </thead>

        <tbody>
          {filteredScenarios.map((s) => (
            <tr key={s.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={() => toggleSelect(s.id)}
                />
              </td>

              <td>
                {s.name}
                {s.isDefault && <span className="badge-default">Default</span>}
              </td>

              <td>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "-"}</td>

              <td>{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : "-"}</td>

              <td className="action-buttons">
                <button
                  className="btn-icon-projects"
                  title="Edit"
                  onClick={() => navigate(`/scenarios/${s.id}`)}
                >
                  <BsPencil />
                </button>

                <button
                  className="btn-icon-projects delete"
                  title="Delete"
                  onClick={() => handleDelete(s.id)}
                >
                  <FiTrash2 />
                </button>

                <button
                  className="btn-icon-projects copy"
                  title="Copy"
                  onClick={() => handleCopy(s.id)}
                >
                  <FiCopy />
                </button>

                <button
                  className="btn-icon-projects copy"
                  title="Export"
                  onClick={() => handleExportScenario(s.id)}
                >
                  <FaFileExport />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showNewScenarioModal && (
        <NewScenarioModal
          onCreate={handleCreateScenario}
          onClose={() => setShowNewScenarioModal(false)}
        />
      )}

      <ScenarioJsonImporter
        inputId="scenario-json-importer"
        tenantId={currentTenantId}
        onImportComplete={async () => {
          await fetchScenarios();
        }}
      />
    </div>
  );
};

export default Scenarios;
